import FormField from '@/components/ui/FormField'
import RedFlagIcon from '@/components/ui/icons/RedFlagIcon'
import { colors, sxPresets } from '@/theme'
import type { PatientDetails, UpdatePatientBody } from '@/services/patients'
import { useGetGPs } from '@/services/gp'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowForward, Edit as EditIcon, ExpandMore, Person as PersonIcon } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  createFilterOptions,
  Divider,
  IconButton,
  Link as MuiLink,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useUnsavedChangesProcessing } from '@/hooks/useUnsavedChangesProcessing'

const patientInfoSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  gender: z.string(),
  phone: z.string(),
  dob: z
    .string()
    .regex(/^\d{4}-\d{1,2}-\d{1,2}$/, 'Expected format: YYYY-MM-DD')
    .or(z.literal('')),
  address1: z.string().max(255, 'Max 255 characters'),
  address2: z.string().max(255, 'Max 255 characters'),
  city: z.string().max(20, 'Max 20 characters'),
  postCode: z.string().max(20, 'Max 20 characters'),
  age: z.string(),
  idVerified: z.boolean(),
  idProvided: z.string().max(100, 'Max 100 characters'),
  gpHash: z.string(),
  redFlag: z.string(),
  redFlagReason: z.string().max(255, 'Max 255 characters'),
  unhappyCustomer: z.boolean(),
  alerts: z.string().max(255, 'Max 255 characters')
})

type PatientInfoFormData = z.infer<typeof patientInfoSchema>

// The patient's current GP only carries hash + name, so postcode is optional
type GpOption = { hash: string; name: string; postcode?: string }

// limit caps how many options the (non-virtualized) listbox mounts into the
// DOM — without it, opening the dropdown renders the entire GP table at once
const gpAutocompleteFilter = createFilterOptions<GpOption>({
  stringify: option => `${option.name} ${option.postcode ?? ''}`,
  limit: 10
})

interface PatientInfoProps {
  patient: PatientDetails
  onSave?: (body: UpdatePatientBody) => Promise<void>
  isSaving?: boolean
  readOnly?: boolean
}

function UserDetailsField({
  label,
  value,
  withIcon = false
}: {
  label: string
  value?: string | null
  withIcon?: boolean
}) {
  return (
    <Box>
      <Typography variant='fieldLabel' sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {withIcon ? <PersonIcon sx={{ fontSize: '16px' }} /> : null}
        {label}
      </Typography>
      <Typography variant='fieldValue' sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
        {value || '-'}
      </Typography>
    </Box>
  )
}

export default function PatientInfo({ patient, onSave, isSaving, readOnly }: PatientInfoProps) {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUserDetailsExpanded, setIsUserDetailsExpanded] = useState(false)

  const defaultValues: PatientInfoFormData = {
    firstName: patient.firstName,
    lastName: patient.lastName,
    gender: patient.gender ? patient.gender.charAt(0).toUpperCase() + patient.gender.slice(1).toLowerCase() : '',
    phone: patient.phone || patient.mobile || '',
    dob: patient.dob,
    address1: patient.primaryAddress?.street || '',
    address2: patient.primaryAddress?.note || '',
    city: patient.primaryAddress?.city || '',
    postCode: patient.primaryAddress?.postalCode || '',
    age: patient.age.toString(),
    idVerified: patient.idVerified ?? false,
    idProvided: patient.idProvided || '',
    gpHash: patient.gp?.hash || '',
    redFlag: patient.hasRedFlag ? 'Yes' : 'No',
    redFlagReason: patient.redFlagReason || '',
    unhappyCustomer: patient.unhappyCustomer,
    alerts: patient.alerts || ''
  }

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, dirtyFields, isDirty }
  } = useForm<PatientInfoFormData>({
    resolver: zodResolver(patientInfoSchema),
    defaultValues
  })

  // Only load the GP list once the agent starts editing
  const gpsQuery = useGetGPs({ enabled: isEditMode && !readOnly })
  const gpOptions: GpOption[] = useMemo(() => {
    const gps = gpsQuery.data ?? []
    // The patient's current GP must exist in the options for the Autocomplete
    // to resolve its value while the full list is still loading
    if (patient.gp && !gps.some(gp => gp.hash === patient.gp?.hash)) {
      return [{ hash: patient.gp.hash, name: patient.gp.name }, ...gps]
    }
    return gps
  }, [gpsQuery.data, patient.gp])

  // Sync default values with any new patient data
  // biome-ignore lint/correctness/useExhaustiveDependencies: this is fine
  useEffect(() => {
    reset(defaultValues)
  }, [patient])

  useUnsavedChangesProcessing(isEditMode && isDirty)

  const handleEdit = () => {
    reset(defaultValues)
    setIsEditMode(true)
  }

  const handleCancel = () => {
    setIsEditMode(false)
    reset(defaultValues)
  }

  const onSubmit = async (data: PatientInfoFormData) => {
    if (readOnly) return
    const body: UpdatePatientBody = {}

    if ('firstName' in dirtyFields) body.first_name = data.firstName
    if ('lastName' in dirtyFields) body.last_name = data.lastName
    if ('dob' in dirtyFields) body.date_of_birth = data.dob || undefined
    if ('gender' in dirtyFields)
      body.gender = data.gender ? (data.gender.toLowerCase() as 'male' | 'female') : undefined
    if ('address1' in dirtyFields) body.street = data.address1 || undefined
    if ('address2' in dirtyFields) body.address_note = data.address2 || undefined
    if ('city' in dirtyFields) body.city = data.city || undefined
    if ('postCode' in dirtyFields) body.postal_code = data.postCode || undefined
    if ('idVerified' in dirtyFields) body.id_verified = data.idVerified
    if ('idProvided' in dirtyFields) body.id_provided = data.idProvided || undefined
    if ('gpHash' in dirtyFields) {
      // Removing a GP without a replacement isn't supported — the agent must pick a new one
      if (!data.gpHash) {
        setError('gpHash', { message: 'Select a GP from the list' })
        return
      }
      body.gp_hash = data.gpHash
    }
    if ('redFlag' in dirtyFields) body.red_flag = data.redFlag === 'Yes'
    if ('redFlagReason' in dirtyFields) body.red_flag_reason = data.redFlagReason || undefined
    if ('unhappyCustomer' in dirtyFields) body.unhappy = data.unhappyCustomer
    if ('alerts' in dirtyFields) body.alerts = data.alerts || undefined

    if (Object.keys(body).length === 0) {
      setIsEditMode(false)
      return
    }

    await onSave?.(body)
    setIsEditMode(false)
  }

  return (
    <Paper
      id='patient-details'
      elevation={1}
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{ border: `1px solid ${colors.border.default}`, overflow: 'hidden' }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Typography variant='sectionTitle'>Patient details</Typography>
        {!isEditMode && !readOnly && (
          <IconButton onClick={handleEdit} size='small' color='primary' type='button'>
            <EditIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        )}
        {isEditMode && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant='primary' type='submit' disabled={!isDirty || isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button variant='ghost' onClick={handleCancel} type='button' disabled={isSaving}>
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 2, px: 3 }}>
        {/* Row 1 */}
        <Box
          sx={{
            ...sxPresets().grid.fourColumn,
            '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
          }}
        >
          <FormField
            name='firstName'
            label='First name'
            control={control}
            isEditMode={isEditMode}
            error={errors.firstName?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='lastName'
            label='Last name'
            control={control}
            isEditMode={isEditMode}
            error={errors.lastName?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='gender'
            label='Gender'
            control={control}
            isEditMode={isEditMode}
            type='select'
            options={['Male', 'Female']}
            error={errors.gender?.message}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
          <FormField
            name='dob'
            label='Date of birth'
            control={control}
            isEditMode={isEditMode}
            type='date'
            error={errors.dob?.message}
            formatDisplayValue={v => v.split('-').reverse().join('/')}
          >
            <PersonIcon sx={{ fontSize: '16px' }} />
          </FormField>
        </Box>

        <Divider />

        {/* Row 2 */}
        <Box
          sx={{
            ...sxPresets().grid.fourColumn,
            '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
          }}
        >
          <FormField
            name='phone'
            label='Mobile'
            control={control}
            isEditMode={isEditMode}
            disabled
            error={errors.phone?.message}
          />
          <Controller
            name='idVerified'
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                  ID verified
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px' }}>
                  <Checkbox
                    checked={field.value}
                    disabled={!isEditMode}
                    onChange={e => field.onChange(e.target.checked)}
                    sx={{ p: 0 }}
                  />
                </Box>
              </Box>
            )}
          />
          <FormField
            name='idProvided'
            label='ID document provided'
            control={control}
            isEditMode={isEditMode}
            error={errors.idProvided?.message}
          />
          <Controller
            name='gpHash'
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant='fieldLabel' sx={{ mb: 1, display: 'block' }}>
                  GP
                </Typography>
                {isEditMode ? (
                  <Autocomplete
                    options={gpOptions}
                    loading={gpsQuery.isLoading}
                    value={gpOptions.find(gp => gp.hash === field.value) ?? null}
                    onChange={(_, gp) => field.onChange(gp?.hash ?? '')}
                    getOptionLabel={option => option.name}
                    isOptionEqualToValue={(option, value) => option.hash === value.hash}
                    filterOptions={gpAutocompleteFilter}
                    size='small'
                    renderOption={(props, option) => {
                      // MUI keys options by their label (GP name), which isn't unique
                      const { key: _key, ...rest } = props
                      return (
                        <Box component='li' key={option.hash} {...rest} sx={{ display: 'block !important' }}>
                          <Typography sx={{ fontSize: '13px' }}>{option.name}</Typography>
                          {option.postcode && (
                            <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>
                              {option.postcode}
                            </Typography>
                          )}
                        </Box>
                      )
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Search GP name...'
                        error={!!errors.gpHash}
                        helperText={errors.gpHash?.message}
                      />
                    )}
                  />
                ) : patient.gp ? (
                  <MuiLink
                    component={Link}
                    to={`/gps/${patient.gp.hash}`}
                    underline='hover'
                    variant='fieldValue'
                    sx={{
                      display: 'block',
                      color: colors.brand.primary,
                      fontWeight: 500,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word'
                    }}
                  >
                    {patient.gp.name}
                  </MuiLink>
                ) : (
                  <Typography variant='fieldValue' sx={{ display: 'block' }}>
                    -
                  </Typography>
                )}
              </Box>
            )}
          />
        </Box>

        <Divider />

        {/* Address row — writes to the patient address used for ID verification,
            not the order's shipping address */}
        <Box
          sx={{
            ...sxPresets().grid.fourColumn,
            '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
          }}
        >
          <FormField
            name='address1'
            label='Address 1'
            control={control}
            isEditMode={isEditMode}
            error={errors.address1?.message}
          />
          <FormField
            name='address2'
            label='Address 2'
            control={control}
            isEditMode={isEditMode}
            error={errors.address2?.message}
          />
          <FormField name='city' label='City' control={control} isEditMode={isEditMode} error={errors.city?.message} />
          <FormField
            name='postCode'
            label='Post code'
            control={control}
            isEditMode={isEditMode}
            error={errors.postCode?.message}
          />
        </Box>

        {/* Row 4 */}
        <Box
          sx={{
            ...sxPresets().grid.fourColumn,
            '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
          }}
        >
          <FormField
            name='redFlag'
            label='Red flag'
            control={control}
            isEditMode={isEditMode}
            type='select'
            options={['Yes', 'No']}
            error={errors.redFlag?.message}
          >
            <RedFlagIcon />
          </FormField>
          <FormField
            name='redFlagReason'
            label='Red flag reason'
            control={control}
            isEditMode={isEditMode}
            error={errors.redFlagReason?.message}
          >
            <RedFlagIcon />
          </FormField>
          <FormField
            name='alerts'
            label='Alerts'
            control={control}
            isEditMode={isEditMode}
            error={errors.alerts?.message}
          />
          <Controller
            name='unhappyCustomer'
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant='fieldLabel' sx={{ mb: 1 }}>
                  Unhappy customer
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '40px', gap: 1 }}>
                  <Checkbox
                    checked={field.value}
                    disabled={!isEditMode}
                    onChange={e => field.onChange(e.target.checked)}
                    sx={{ p: 0 }}
                  />
                  <Typography variant='fieldValue' sx={{ color: colors.text.secondary, fontSize: '11px' }}>
                    Suppresses review invitations after delivery
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>
      </Box>

      {/* User details */}
      <Box sx={{ bgcolor: colors.background.surface, px: 3, py: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Typography variant='sectionTitle'>User details</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              component={Link}
              to={`/users/${patient.userEmail}`}
              variant='link'
              endIcon={<ArrowForward sx={{ fontSize: '16px' }} />}
              type='button'
            >
              View user
            </Button>
            <IconButton
              size='small'
              type='button'
              onClick={() => setIsUserDetailsExpanded(expanded => !expanded)}
              sx={{
                ...sxPresets().expandButton,
                transform: isUserDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isUserDetailsExpanded}>
          <Box
            sx={{
              ...sxPresets().grid.fourColumn,
              mt: 3,
              '& > *:not(:last-child)': { borderRight: `1px solid ${colors.border.default}`, pr: 3 }
            }}
          >
            <UserDetailsField label='First name' value={patient.userDetails.firstName} withIcon />
            <UserDetailsField label='Last name' value={patient.userDetails.lastName} withIcon />
            <UserDetailsField label='Email Address' value={patient.userDetails.email} />
            <UserDetailsField label='Mobile Number' value={patient.userDetails.mobile || patient.mobile} />
          </Box>
        </Collapse>
      </Box>
    </Paper>
  )
}
